import { useCallback } from 'react';
import type { SessionsSaveSessionState } from '@/features/sessions';
import { usePermissions } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useSaveToProjectEnabled } from '@/shared/hooks';
import { saveAudienceSessionAsArtifact } from '../api';

/**
 * Resolves whether the current user can save the active audience session to
 * a project and, when they can, exposes a ready-to-call `mintArtifact` bound
 * to the contextId. Returns `null` when any gate fails (no contextId, no
 * workspace, no permission, flag off) so the consumer can simply skip
 * rendering the save UI.
 */
export function useSaveSessionToProject(contextId: string): SessionsSaveSessionState | null {
  const enabled = useSaveToProjectEnabled();
  const { canManageAudiences } = usePermissions();
  const workspaceId = useCurrentWorkspaceId();

  const mintArtifact = useCallback(
    () => saveAudienceSessionAsArtifact(contextId, workspaceId ?? ''),
    [contextId, workspaceId],
  );

  if (!enabled || !canManageAudiences || !workspaceId || !contextId) {
    return null;
  }

  return { workspaceId, mintArtifact };
}
